import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { LazyImage } from './LazyImage';
import { ProBadge } from './ProBadge';
import { ListingStatusBadge } from './ListingStatusBadge';
import { FeaturedBadge } from './FeaturedBadge';
import { FavoriteButton } from './FavoriteButton';
import { useCurrency } from '../context/CurrencyContext';
import { formatDualCurrency } from '../utils/currency';
import { getCategoryName, getSubcategoryName } from '../utils/categoryNames';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Single listing card with two visual variants:
 *   - `grid`: existing card layout (default, used by BrowsePage grid)
 *   - `list`: compact horizontal row (mobile-first, more items per screen)
 *
 * Both variants share the same data, badges and click targets — only the
 * positioning/typography differs. Keeping it in one component means future
 * field additions (e.g. condition badge) ship in both views automatically.
 */
export const ListingCard = ({ listing, variant = 'grid' }) => {
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const price = formatDualCurrency(listing.price_mga, secondaryCurrency);

  const isNewBadge =
    listing.created_at &&
    !listing.is_featured &&
    Date.now() - new Date(listing.created_at).getTime() < 172800000;
  const isPro = listing.seller_type === 'business';

  if (variant === 'list') {
    return (
      <div
        className="listing-card flex gap-3 p-3 bg-white border border-border rounded-xl hover:border-primary/40 transition-colors relative"
        data-testid={`listing-${listing.listing_id}`}
      >
        <Link to={`/listings/${listing.listing_id}`} className="flex-shrink-0">
          <div
            className="w-32 h-24 sm:w-36 sm:h-28 rounded-lg bg-surface overflow-hidden"
            data-testid="listing-thumb"
          >
            {listing.images && listing.images.length > 0 ? (
              <LazyImage
                src={`${API}/files/${listing.images[0]}`}
                alt={listing.title}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] text-text_muted">
                {t('common.noImage')}
              </div>
            )}
          </div>
        </Link>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <Link to={`/listings/${listing.listing_id}`} className="block">
                <p
                  className="text-base font-bold text-primary leading-tight"
                  data-testid="listing-price"
                >
                  {price.mga}
                </p>
                <p className="text-[11px] text-text_muted mb-1">{price.fmg}</p>
                <h3
                  className="font-semibold text-sm truncate hover:text-primary"
                  data-testid="listing-title"
                >
                  {listing.title}
                </h3>
              </Link>
              <p className="text-xs text-text_muted truncate" data-testid="listing-meta">
                <Link
                  to={`/listings/${listing.listing_id}`}
                  className="hover:text-primary"
                >
                  {listing.city}
                </Link>
                <Link
                  to={`/browse?category=${encodeURIComponent(listing.category)}`}
                  className="font-medium text-primary hover:underline"
                  data-testid="listing-meta-category"
                >
                  {' • '}{getCategoryName(listing.category, t)}
                </Link>
                {listing.subcategory && (
                  <Link
                    to={`/browse?category=${encodeURIComponent(listing.category)}&subcategory=${encodeURIComponent(listing.subcategory)}`}
                    className="text-text_muted hover:text-primary hover:underline"
                    data-testid="listing-meta-subcategory"
                  >
                    {' • '}{getSubcategoryName(listing.subcategory, t)}
                  </Link>
                )}
              </p>
            </div>
            <div className="flex-shrink-0">
              <FavoriteButton listingId={listing.listing_id} />
            </div>
          </div>

          <div className="mt-auto pt-1.5 flex items-center gap-1.5 flex-wrap">
            {listing.is_featured && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-warning-soft text-warning border border-warning">
                ★ Featured
              </span>
            )}
            {isNewBadge && (
              <span className="inline-block px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-accent text-white tracking-wider">
                {t('common.newBadge')}
              </span>
            )}
            {isPro && <ProBadge size="thumb" />}
            <ListingStatusBadge listing={listing} />
            {listing.created_at && (
              <span className="ml-auto text-[10px] text-text_muted">
                {formatDistanceToNow(new Date(listing.created_at), { addSuffix: true })}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Grid variant (default) ───
  return (
    <div
      className="listing-card border border-border bg-white rounded-xl overflow-hidden relative"
      data-testid={`listing-${listing.listing_id}`}
    >
      {listing.is_featured && <FeaturedBadge />}

      {isNewBadge && (
        <span
          className="absolute top-2 left-2 z-10 bg-accent text-white text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full shadow-sm"
          data-testid="new-badge"
        >
          {t('common.newBadge')}
        </span>
      )}

      {isPro && (
        <ProBadge
          size="thumb"
          title={t('sellerType.business')}
          className={`absolute ${listing.is_featured || isNewBadge ? 'top-9' : 'top-2'} left-2 z-10 shadow-sm`}
        />
      )}

      <div className="absolute top-2 right-2 z-10">
        <FavoriteButton listingId={listing.listing_id} />
      </div>

      <Link to={`/listings/${listing.listing_id}`}>
        <div className="aspect-[4/3] bg-surface">
          {listing.images && listing.images.length > 0 ? (
            <LazyImage
              src={`${API}/files/${listing.images[0]}`}
              alt={listing.title}
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-text_muted">
              {t('common.noImage')}
            </div>
          )}
        </div>
      </Link>

      <div className="p-4">
        <Link to={`/listings/${listing.listing_id}`}>
          <h3
            className="font-semibold font-heading text-sm mb-1 truncate hover:text-primary"
            data-testid="listing-title"
          >
            {listing.title}
          </h3>
        </Link>
        <p className="text-text_muted text-xs mb-2 truncate" data-testid="listing-meta">
          {listing.city}
          <Link
            to={`/browse?category=${encodeURIComponent(listing.category)}`}
            className="font-semibold text-primary hover:underline"
            data-testid="listing-meta-category"
          >
            {' • '}{getCategoryName(listing.category, t)}
          </Link>
          {listing.subcategory && (
            <Link
              to={`/browse?category=${encodeURIComponent(listing.category)}&subcategory=${encodeURIComponent(listing.subcategory)}`}
              className="text-text_muted hover:text-primary hover:underline"
              data-testid="listing-meta-subcategory"
            >
              {' • '}{getSubcategoryName(listing.subcategory, t)}
            </Link>
          )}
        </p>
        <p className="text-lg font-bold text-primary" data-testid="listing-price">
          {price.mga}
        </p>
        <p className="text-xs text-text_muted">{price.fmg}</p>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          {listing.created_at && (
            <p className="text-xs text-text_muted">
              {formatDistanceToNow(new Date(listing.created_at), { addSuffix: true })}
            </p>
          )}
          <ListingStatusBadge listing={listing} />
        </div>
      </div>
    </div>
  );
};
